import React, { useEffect, useMemo } from 'react'
import { Box, TextField, Button, Grid, InputLabel, InputAdornment, Checkbox, FormControlLabel, FormControl,
  Select, MenuItem } from '@mui/material';
import { useForm, Controller } from "react-hook-form";
import { useSnackbar } from 'notistack';
import DatePicker from '@mui/lab/DatePicker';
import DateAdapter from '@mui/lab/AdapterDateFns';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import ControlPointIcon from '@mui/icons-material/ControlPoint';
import { saveCoupon } from 'services/coupons';
import { parse } from 'date-fns';

const AddCoupon = ({ couponDetails = null, onCouponAdded}) => {
  const defaultFormFields = useMemo(() => ({
    code: null,
    validityStartDate: null,
    validityEndDate: null,
    limitPerUser: 0,
    discountPercent: 0,
    description: '',
    singleUse: false,
    usageLimit: 0,
    targetEntityType: '',
  }),[]);

  const { enqueueSnackbar } = useSnackbar();
  const { handleSubmit, control, formState: { errors }, watch, reset } = useForm({
    mode: 'onTouched',
    defaultValues: {...defaultFormFields}
  });


  useEffect(() => {
    if(couponDetails){
      const data = {...(Object.fromEntries(Object.entries(defaultFormFields).map(([key, value]) => {
        return [key, couponDetails[key] || value];
      })))};

      try {
        data.validityStartDate = parse(data.validityStartDate, 'dd-MM-yyyy', new Date());
        data.validityEndDate = parse(data.validityEndDate, 'dd-MM-yyyy', new Date());
      } catch(e) {
        data.validityStartDate  = null;
        data.validityEndDate  = null;
      }
      reset({...data});
    }
  }, [couponDetails, defaultFormFields, reset]);
  
  const getConstDbFormat = (date = new Date()) => {
    return `${date.getDate().toString().padStart(2, '0')}-${(date.getMonth()+1).toString().padStart(2, '0')}-${date.getFullYear()}` 
  };

  const onSubmit = (data) => {
    saveCoupon({
        ...data,
        targetEntityId: data?.targetEntityId?.libraryId,
        validityStartDate: getConstDbFormat(data.validityStartDate),
        validityEndDate: getConstDbFormat(data.validityEndDate),
        id: couponDetails?.id,
        singleUse: data?.singleUse ? data?.singleUse : false,
      }).then(({response, error}) => {
        if(response){
          enqueueSnackbar((`Coupon ${couponDetails?.id ? 'updated' : 'added'} successfully`), {variant: 'success'});
          reset({...defaultFormFields});
          onCouponAdded();
        } else {
          enqueueSnackbar((response?.message || error), {variant: 'error'});
        }
      });
  }

  return (
    <Box noValidate component="form"  onSubmit={handleSubmit(onSubmit)} sx={{ mt: 2 }} >
      <Grid container columnSpacing={2} rowSpacing={2} justifyContent="center">
        <Grid item xs={12} sm={12} md={8} lg={8}  >
          <Grid item container columnSpacing={2}>

            <Grid item xs={12} sm={12} md={12} lg={12} sx={{ mt: 2 }}>
              <Controller
                control={control}
                name="targetEntityType"
                id="targetEntityType"
                required
                rules={{ required: "Select property type" }}
                render={({field}) => (
                  <FormControl {...field} required fullWidth error={!!errors?.targetEntityType}>
                    <InputLabel id="targetEntityType-label" size="small">Property Type</InputLabel>
                    <Select
                      labelId="targetEntityType-label"
                      id="targetEntityType-select-id"
                      label="Property Type"
                      name="targetEntityType"
                      required
                      size="small"
                      value={field.value}
                      onChange={field.onChange}
                    >
                      <MenuItem value='library'>Library</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            <Grid item xs={12} sm={12} md={12} lg={12} sx={{mt: 2}}>
              <Controller
                control={control}
                name="discountPercent"
                id="discountPercent"
                rules={{ required: "Enter discount percent",
                  max: {value: 100, message: "Maximum 100% allowed" }
                }}
                render={({field}) => (
                  <TextField
                    {...field}
                    type="number"
                    margin="normal"
                    label="Discount Percent"
                    required
                    inputProps={{
                      max: '100'
                    }}
                    error={!!errors?.discountPercent}
                    helperText={errors.discountPercent?.message}
                    size="small"
                    fullWidth
                    InputProps={{
                      endAdornment: <InputAdornment position="start">%</InputAdornment>,
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={12} md={12} lg={12} >
              <Controller
                control={control}
                name="description"
                id="description"
                rules={{ required: "Enter description" }}
                render={({field}) => (
                  <TextField
                    {...field}
                    margin="normal"
                    label="Description"
                    required
                    error={!!errors?.description}
                    size="small"
                    fullWidth
                    multiline
                    minRows={2}
                    maxRows={6}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={12} md={12} lg={12} sx={{mt: 2.5}}>
              <Controller
                control={control}
                name="validityStartDate"
                id="validityStartDate"
                required
                rules={{ 
                  required: "Select validity start date",
                  validate: (value) => value?.getTime() >= new Date().getTime()  || "Date should be greater than today's date"
                }}
                render={({field}) => ( 
                  <LocalizationProvider dateAdapter={DateAdapter} >
                    <DatePicker
                      {...field}
                      required
                      label="Validity Start Date"
                      inputFormat="dd/MM/yyyy"
                      clearable={false}
                      minDate={field?.value ? (field.value.getTime() > new Date().getTime() ? new Date() : field.value)  : new Date()}
                      value={field.value}
                      onChange={field.onChange}
                      error={!!errors?.validityStartDate}  
                      renderInput={(params) => (
                        <TextField 
                          required
                          value={field.value}
                          {...params} 
                          error={!!errors?.validityStartDate}
                          helperText={errors?.validityStartDate?.message}  
                          size="small" 
                          sx={{width: '100%'}} 
                        />
                      )}
                    />
                  </LocalizationProvider>
                )}
              />
            </Grid>

            <Grid item xs={12} sm={12} md={12} lg={12} sx={{mt: 2.5}}>
              <Controller
                control={control}
                name="validityEndDate"
                id="validityEndDate"
                required
                rules={{ required: "Select validity end date",
                  // validate: (value) => value?.getTime() >= new Date().getTime()  || "Date should be greater than today's date",
                  validate: (value) => value?.getTime() >= watch('validityStartDate').getTime()  || "Validity End Date should be greater than Validity Start Date ",
                }}
                render={({field}) => (
                  <LocalizationProvider dateAdapter={DateAdapter} >
                    <DatePicker
                      {...field}
                      required
                      label="Validity End Date"
                      inputFormat="dd/MM/yyyy"
                      clearable={false}
                      minDate={new Date()}
                      value={field.value}
                      onChange={field.onChange}
                      renderInput={(params) => (
                        <TextField 
                          required
                          value={field.value}
                          {...params} 
                          error={!!errors?.validityEndDate}  
                          helperText={errors?.validityEndDate?.message}
                          size="small" 
                          sx={{width: '100%'}} 
                        />
                      )}
                    />
                  </LocalizationProvider>
                )}
              />
            </Grid>

            <Grid item xs={12} sm={12} md={12} lg={12} >
              <Controller
                control={control}
                name="singleUse"
                id="singleUse"
                // rules={{ required: "Enter single use" }}
                // error={!!errors?.singleUse}
                render={({field}) => (
                  <FormControl
                    name="singleUse"
                    component="fieldset"
                    sx={{ mt: 1.5 }}
                    variant="standard"
                    {...field}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox {...field} checked={field.value} onChange = {field.onChange}
                        name="singleUse"
                        />
                      }
                      labelPlacement="start"
                      label="Single Use"
                    />
                  </FormControl>
                )}
              />
            </Grid>

            { watch('singleUse') ? null : (<Grid item xs={12} sm={12} md={12} lg={12} >
              <Controller
                control={control}
                name="limitPerUser"
                id="limitPerUser"
                rules={{ required: "Enter limit per user",
                  pattern: {
                    value: /^[0-9]{0,100}$/i,
                    message: "Enter valid number",
                  }, 
                }}
                render={({field}) => (
                  <TextField
                    {...field}
                    margin="normal"
                    label="Limit per User"
                    required
                    error={!!errors?.limitPerUser}
                    helperText={errors?.limitPerUser?.message}
                    size="small"
                    fullWidth
                  />
                )}
              />
            </Grid>)}

            <Grid item xs={12} sm={12} md={12} lg={12} >
              <Controller
                control={control}
                name="usageLimit"
                id="usageLimit"
                rules={{ required: "Enter usage limit",
                  pattern: {
                    value: /^[0-9]{0,100}$/i,
                    message: "Enter valid number",
                  }
                }}
                render={({field}) => (
                  <TextField
                    {...field}
                    margin="normal"
                    label="Usage Limit"
                    required
                    error={!!errors?.usageLimit}
                    helperText={errors?.usageLimit?.message}
                    size="small"
                    fullWidth
                  />
                )}
              />
            </Grid>

            <Grid
              container
              direction="row"
              justifyContent="center"
              alignItems="center"
            >
              <Button
                type="submit"
                variant="contained"
                sx={{ mt: 3, mb: 2, textTransform: 'inherit' }}
                startIcon={ couponDetails ? <ModeEditIcon />: <ControlPointIcon />}
                color="primary"
              >
                {couponDetails ? 'Update' : 'Create'}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
}
 
export default AddCoupon;
